import { useEffect, useState } from 'react';
import { TopBar } from '../components/TopBar';
import { Nav } from '../components/Nav';
import { Hero } from '../components/Hero';
import { QuarterlyDispatch } from '../components/QuarterlyDispatch';
import { Ventures } from '../components/Ventures';
import { Now } from '../components/Now';
import { Writing } from '../components/Writing';
import { Contact } from '../components/Contact';
import { Footer } from '../components/Footer';
import { KSecret } from '../components/KSecret';
import { CursorSpotlight } from '../components/CursorSpotlight';
import { staticData, staticDispatch } from '../lib/static-data';
import type { Dispatch, SiteData } from '../lib/types';
import {
  getCompanies,
  getCurrentDispatch,
  getNow,
  getWritingTeasers,
  getCircuit,
} from '../lib/data';

export function HomePage() {
  const [data, setData] = useState<SiteData>(() => ({ ...staticData }));
  const [dispatch, setDispatch] = useState<Dispatch>(staticDispatch as Dispatch);
  const [secret, setSecret] = useState(false);

  // Load live data (Supabase → fallback to static). No loading state: the
  // static data renders immediately, live swaps in if/when it arrives.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [companies, now, writing, circuit, dispatchRow] = await Promise.all([
        getCompanies(),
        getNow(),
        getWritingTeasers(5),
        getCircuit(),
        getCurrentDispatch(),
      ]);
      if (cancelled) return;
      setData({
        companies,
        now,
        writing,
        circuit,
        yakitori: staticData.yakitori, // static for now — move to DB later
      });
      setDispatch(dispatchRow);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // K-key easter egg. Ignore while typing in inputs.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement | null)?.tagName ?? '';
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      if (e.key === 'k' || e.key === 'K') setSecret((s) => !s);
      if (e.key === 'Escape') setSecret(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <div
      style={{
        background: 'var(--paper)',
        color: 'var(--ink)',
        fontFamily: 'var(--font-mono)',
        minHeight: '100vh',
        position: 'relative',
        overflow: 'hidden',
        fontSize: 12,
        lineHeight: 1.55,
      }}
    >
      <CursorSpotlight />
      <TopBar />
      <Nav />
      <Hero />
      <QuarterlyDispatch circuit={data.circuit} dispatch={dispatch} />
      <Ventures companies={data.companies} />
      <Now items={data.now} asOf={new Date()} />
      <Writing posts={data.writing} />
      <Contact />
      <Footer />
      <KSecret open={secret} onClose={() => setSecret(false)} spots={data.yakitori} />
    </div>
  );
}
